import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PenTool, Code, TestTube, Rocket, Monitor, Shield, ShieldCheck, Lock, Eye, ArrowRight } from 'lucide-react';

const slides = [
  {
  overline: 'OUR APPROACH',
  heading: 'Built Secure\nfrom Line One.',
  body: 'Most companies hire one firm to build their software and another to test whether it is secure. That gap between building and protecting is where breaches happen, where compliance fails, and where money gets wasted fixing problems that should never have existed. Flynaut closes that gap.',
  buildIcon: null,
  protectIcon: null,
  isIntro: true,
  },
  {
  overline: '01 - ARCHITECTURE',
  heading: 'Threat modeling\nbegins with\nsystem design.',
  body: 'Security decisions are made at the whiteboard, not after deployment. Every architecture choice is evaluated for both performance and vulnerability.',
  buildIcon: PenTool,
  protectIcon: Shield,
  },
  {
  overline: '02 - DEVELOPMENT',
  heading: 'Secure coding\nstandards in\nevery sprint.',
  body: 'Our developers and security engineers sit in the same standups. Code reviews check for functionality and exploitability simultaneously.',
  buildIcon: Code,
  protectIcon: ShieldCheck,
  },
  {
  overline: '03 - TESTING',
  heading: 'Penetration\ntesting alongside\nQA.',
  body: 'We don\'t wait for a finished product to run security tests. Pen testing happens in parallel with QA - catching issues when they\'re cheap to fix.',
  buildIcon: TestTube,
  protectIcon: Lock,
  },
  {
  overline: '04 - DEPLOYMENT',
  heading: 'Zero-trust\ndeployment\npipelines.',
  body: 'Every deployment pipeline enforces zero-trust principles. Automated security gates ensure nothing ships that shouldn\'t.',
  buildIcon: Rocket,
  protectIcon: Eye,
  },
  {
  overline: '05 - OPERATIONS',
  heading: 'Continuous\nmonitoring from\nday one.',
  body: 'The moment your application goes live, our SOC is watching. Threat detection, incident response, and continuous improvement - not an afterthought.',
  buildIcon: Monitor,
  protectIcon: Shield,
  },
];

const TOTAL = slides.length;

function Slide({ slide, index, scrollYProgress }) {
  const slideStart = index / TOTAL;
  const slideEnd = (index + 1) / TOTAL;
  const progress = useTransform(scrollYProgress, [slideStart, slideEnd], [0, 1]);

  const opacity = useTransform(progress, [0, 0.25, 0.75, 1], [0, 1, 1, 0]);
  const y = useTransform(progress, [0, 0.25, 0.75, 1], [40, 0, 0, -40]);
  const scale = useTransform(progress, [0, 0.25, 0.75, 1], [0.97, 1, 1, 0.97]);

  return (
  <motion.div
  style={{ opacity, y, scale }}
  className="absolute inset-0 pt-[80px] pb-[70px] flex items-center justify-center px-6"
  >
  <div className="max-w-5xl mx-auto w-full text-center">
  <p className="text-overline text-orange-500 mb-5 tracking-[0.2em]">
  {slide.overline}
  </p>

  <h2
  className="font-display font-black text-white mb-6 whitespace-pre-line leading-[0.9] tracking-tighter"
  style={{ fontSize: 'clamp(3rem, 2rem + 7vw, 7rem)' }}
  >
  {slide.heading}
  </h2>

  <p className="text-[#A0AEC0] text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
  {slide.body}
  </p>

  {slide.buildIcon && slide.protectIcon && (
  <div className="flex items-center justify-center gap-6">
  <div className="flex items-center gap-4">
  <div className="w-14 h-14 rounded-xl border border-orange-500/30 bg-orange-500/10 flex items-center justify-center">
  <slide.buildIcon size={24} className="text-orange-500" />
  </div>
  <span className="text-white/30 text-xs font-medium uppercase tracking-widest">Build</span>
  </div>
  <div className="w-12 flex items-center">
  <div className="w-full border-t-2 border-dashed border-orange-500/40" />
  </div>
  <div className="flex items-center gap-4">
  <span className="text-white/30 text-xs font-medium uppercase tracking-widest">Protect</span>
  <div className="w-14 h-14 rounded-xl border border-orange-500/30 bg-orange-500/10 flex items-center justify-center">
  <slide.protectIcon size={24} className="text-orange-500" />
  </div>
  </div>
  </div>
  )}

  {slide.isIntro && (
  <a
  href="/about"
  data-testid="build-protect-cta"
  className="group inline-flex items-center gap-2.5 border border-white/20 text-white hover:border-white/40 hover:bg-white/5 rounded-full px-8 py-4 text-base font-medium transition-all duration-200"
  >
  See Our Approach <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  )}
  </div>
  </motion.div>
  );
}

function StageDot({ index, scrollYProgress }) {
  const dotOpacity = useTransform(
  scrollYProgress,
  [index / TOTAL, (index + 0.5) / TOTAL],
  [0.3, 1]
  );
  return (
  <motion.div
  style={{ opacity: dotOpacity }}
  className="w-1.5 h-1.5 rounded-full bg-orange-500"
  />
  );
}

function ScrollHint({ scrollYProgress }) {
  const hintOpacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);
  return (
  <motion.div
  className="absolute bottom-16 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
  style={{ opacity: hintOpacity }}
  >
  <span className="text-white/30 text-xs uppercase tracking-widest">Scroll</span>
  <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
  </motion.div>
  );
}

export default function BuildProtect() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
  target: containerRef,
  offset: ['start start', 'end end'],
  });

  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
  <section
  ref={containerRef}
  data-testid="build-protect-section"
  className="relative bg-[#0A0F1C]"
  style={{ height: `${TOTAL * 150}vh`, zIndex: 0 }}
  >
  <div className="sticky top-0 h-screen overflow-hidden" style={{ zIndex: 1 }}>
  <div className="absolute inset-0 noise-subtle noise-dark" />
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(245,132,31,0.04)_0%,transparent_60%)]" />

  {slides.map((slide, i) => (
  <Slide key={i} slide={slide} index={i} scrollYProgress={scrollYProgress} />
  ))}

  {/* Progress bar */}
  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-48">
  <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
  <motion.div
  className="h-full bg-orange-500 rounded-full"
  style={{ width: progressWidth }}
  />
  </div>
  <div className="flex items-center justify-between mt-2.5">
  {slides.map((_, i) => (
  <StageDot key={i} index={i} scrollYProgress={scrollYProgress} />
  ))}
  </div>
  </div>

  <ScrollHint scrollYProgress={scrollYProgress} />
  </div>
  </section>
  );
}
